import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Num, T, Var } from 'gt-react';
import { PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import type { UploadLayout } from '@erp/rpc';

import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LayoutEditor } from './LayoutEditor';
import {
  draftFromDefinition,
  draftFromLayout,
  type LayoutDraft,
} from './layoutForm';
import { HUBBELL_LAYOUT, HUBBELL_LAYOUT_NAME } from './hubbellLayout';
import { ROCKWELL_LAYOUT, ROCKWELL_LAYOUT_NAME } from './rockwellLayout';
import { STELPRO_LAYOUT, STELPRO_LAYOUT_NAME } from './stelproLayout';
import type { ResultsSheet } from './sheetCells';
import { ToolCard } from './ToolCard';
import { usePricingPermissions } from './usePricingPermissions';

type Editing =
  | { mode: 'closed' }
  | { mode: 'edit'; layoutId: string | null; draft: LayoutDraft };

const CLOSED: Editing = { mode: 'closed' };

/** Built-in supplier layouts a new layout can start from. */
const TEMPLATES = [
  { name: ROCKWELL_LAYOUT_NAME, definition: ROCKWELL_LAYOUT },
  { name: STELPRO_LAYOUT_NAME, definition: STELPRO_LAYOUT },
  { name: HUBBELL_LAYOUT_NAME, definition: HUBBELL_LAYOUT },
];
const BLANK: LayoutDraft = { name: '', includeWhenAnyFilled: '', columns: [] };

interface Props {
  layouts: UploadLayout[];
  unreadableLayoutIds: string[];
  sheet: ResultsSheet | null;
  lookupSheet: ResultsSheet | null;
  onSaved: (layout: UploadLayout) => void;
}

function showError(error: Error): void {
  toast.error(error.message);
}

/** Deletes a layout; the rules scoped to it go with it (FK cascade). */
function useDeleteLayout() {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.pricing.uploadLayouts.delete.mutationOptions(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: orpc.pricing.uploadLayouts.list.key(),
      });
      await queryClient.invalidateQueries({
        queryKey: orpc.pricing.uploadRules.list.key(),
      });
    },
    onError: showError,
  });
}

function LayoutItem({
  layout,
  onEdit,
  onDelete,
  isBusy,
}: {
  layout: UploadLayout;
  onEdit: (() => void) | null;
  onDelete: (() => void) | null;
  isBusy: boolean;
}) {
  return (
    <li className="flex items-center gap-2 border-b py-1.5 text-sm last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{layout.name}</div>
        <div className="text-muted-foreground text-xs">
          <T context="number of mapped upload columns in a supplier layout">
            <Num>{layout.definition.columns.length}</Num> columns mapped
          </T>
        </div>
      </div>
      {onEdit ? (
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <PencilSimpleIcon className="h-4 w-4" />
          <T context="button that edits a supplier upload layout">Edit</T>
        </Button>
      ) : null}
      {onDelete ? (
        <Button size="sm" variant="ghost" disabled={isBusy} onClick={onDelete}>
          <TrashIcon className="h-4 w-4" />
          <T context="button that deletes a supplier upload layout">Delete</T>
        </Button>
      ) : null}
    </li>
  );
}

function UnreadableLayouts({
  ids,
  onDelete,
  isBusy,
}: {
  ids: string[];
  onDelete: ((id: string) => void) | null;
  isBusy: boolean;
}) {
  return (
    <>
      {ids.map((id) => (
        <li
          key={id}
          className="text-destructive flex items-center gap-2 py-1.5 text-xs"
        >
          <span className="flex-1">
            <T context="a stored supplier layout this version cannot read">
              A layout saved by another version can’t be read. Downloads are
              paused until it is deleted.
            </T>
          </span>
          {onDelete ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={isBusy}
              onClick={() => onDelete(id)}
            >
              <TrashIcon className="h-4 w-4" />
              <T context="button that deletes an unreadable supplier layout">
                Delete
              </T>
            </Button>
          ) : null}
        </li>
      ))}
    </>
  );
}

/**
 * The supplier layouts: one per input workbook shape, all producing the same
 * upload. Listed in a compact card; the editor, with its preview on the
 * dropped file, opens in a wide dialog.
 */
export function LayoutsPanel({
  layouts,
  unreadableLayoutIds,
  sheet,
  lookupSheet,
  onSaved,
}: Props) {
  const { canCreate, canUpdate, canDelete } = usePricingPermissions();
  const remove = useDeleteLayout();
  const [editing, setEditing] = useState<Editing>(CLOSED);
  const startNew = (draft: LayoutDraft) =>
    setEditing({ mode: 'edit', layoutId: null, draft });
  const isEmpty = layouts.length === 0 && unreadableLayoutIds.length === 0;

  return (
    <ToolCard
      title={
        <T context="heading of the supplier upload layouts card">
          Supplier Layouts
        </T>
      }
      description={
        <T context="explanation under the supplier upload layouts heading">
          How each supplier’s workbook fills the upload columns.
        </T>
      }
      action={
        canCreate ? (
          <Button size="sm" variant="outline" onClick={() => startNew(BLANK)}>
            <PlusIcon className="h-4 w-4" />
            <T context="button that starts a blank upload layout">New</T>
          </Button>
        ) : null
      }
    >
      <ul>
        {layouts.map((layout) => (
          <LayoutItem
            key={layout.id}
            layout={layout}
            isBusy={remove.isPending}
            onEdit={
              canUpdate
                ? () =>
                    setEditing({
                      mode: 'edit',
                      layoutId: layout.id,
                      draft: draftFromLayout(layout),
                    })
                : null
            }
            onDelete={canDelete ? () => remove.mutate({ id: layout.id }) : null}
          />
        ))}
        <UnreadableLayouts
          ids={unreadableLayoutIds}
          isBusy={remove.isPending}
          onDelete={canDelete ? (id) => remove.mutate({ id }) : null}
        />
      </ul>
      {isEmpty ? (
        <p className="text-muted-foreground py-1 text-sm">
          <T context="empty state of the supplier layouts card">
            No layouts saved yet. The built-in Rockwell, Stelpro and Hubbell
            layouts are used until you save your own.
          </T>
        </p>
      ) : null}
      {canCreate ? (
        <div className="mt-1 flex flex-col items-start">
          {TEMPLATES.map((template) => (
            <Button
              key={template.name}
              size="sm"
              variant="ghost"
              onClick={() =>
                startNew(
                  draftFromDefinition(template.name, template.definition),
                )
              }
            >
              <PlusIcon className="h-4 w-4" />
              <T context="button that starts a new upload layout from a supplier template; the variable is the supplier">
                New From <Var>{template.name}</Var> Template
              </T>
            </Button>
          ))}
        </div>
      ) : null}

      <Dialog
        open={editing.mode === 'edit'}
        onOpenChange={(open) => !open && setEditing(CLOSED)}
      >
        <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <T context="title of the supplier layout editor dialog">
                Supplier Layout
              </T>
            </DialogTitle>
          </DialogHeader>
          {editing.mode === 'edit' ? (
            <LayoutEditor
              key={editing.layoutId ?? 'new'}
              layoutId={editing.layoutId}
              initial={editing.draft}
              sheet={sheet}
              lookupSheet={lookupSheet}
              onCancel={() => setEditing(CLOSED)}
              onSaved={(layout) => {
                setEditing(CLOSED);
                onSaved(layout);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </ToolCard>
  );
}
